import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import type { EntityFilesPopoverRow } from './entityFilesPopover.types';
import FormatColorTextIcon from '@mui/icons-material/FormatColorText';
import { ChevronRightIcon, DeleteIcon, DownloadIcon, EditIcon } from '@/components/ui/Icons';
import { IconButton } from '@/components/ui/IconButton/IconButton';
import { Switch } from '@/components/ui/Switch/Switch';
import { Badge } from '@/components/ui/Badge/Badge';
import { ContextMenu } from '@/components/ui/ContextMenu/ContextMenu';
import { ContextMenuSubmenu } from '@/components/ui/ContextMenu/ContextMenuSubmenu';
import { copyPlainTextToClipboard } from '@/lib/copyPlainTextToClipboard';
import {
  formatFileDate,
  formatProjectFileMetricsTrailing,
  getFileTypeLabel,
  isAudioFileName,
  isRecentlyUploaded,
  splitFileNameStemAndSuffix,
} from './projectFilesPopoverUtils';
import { PROJECT_FILES_ROW_CONTEXT_MENU_Z } from './projectFilesPopoverConstants';
import { PROJECT_FILE_TYPE_VALUES, PROJECT_FILE_TYPE_TEXT } from './projectFileTypeConstants';
import { getProjectFileTypeBadgeColors, getProjectFileTypeMenuDotColor } from './projectFileTypeBadgeColors';
import { getStatusBadgeColorByBase } from '@/styles/projectColorTokens';
import { ProjectFileAudioPlayer } from './ProjectFileAudioPlayer';
import type { ProjectFileLinkSelectionProps, ProjectFileDraftItem } from './ProjectFilesPopover.types';

export type FileLinkSelection = {
  selected: boolean;
  onToggle: (nextSelected: boolean) => void;
};

export const ProjectFilesPopoverFileRow = memo(function ProjectFilesPopoverFileRow({
  file,
  onDelete,
  isDeleting,
  deleteDisabled,
  linkSelection,
  /** Общий API выбора файлов для письма — один объект на все строки, без новых колбэков в map */
  fileLinkSelectionApi,
  onRequestRename,
  onRequestRenameById,
  onCopiedLinkSuccess,
  onCopiedLinkError,
  fileTypeMenuEnabled = false,
  onPatchFile,
  patchProjectFile,
}: {
  file: EntityFilesPopoverRow;
  onDelete: (fileId: string) => void;
  isDeleting: boolean;
  deleteDisabled: boolean;
  linkSelection?: FileLinkSelection;
  fileLinkSelectionApi?: ProjectFileLinkSelectionProps;
  onRequestRename?: () => void;
  onRequestRenameById?: (fileId: string) => void;
  onCopiedLinkSuccess?: () => void;
  onCopiedLinkError?: () => void;
  /** Файлы проекта: подменю типа и PATCH file_type */
  fileTypeMenuEnabled?: boolean;
  onPatchFile?: (patch: { file_type?: string | null }) => void;
  patchProjectFile?: (fileId: string, patch: { file_type?: string | null }) => void;
}) {
  const [contextMenuPos, setContextMenuPos] = useState<{ x: number; y: number } | null>(null);
  const [fileTypeSubmenuAnchorEl, setFileTypeSubmenuAnchorEl] = useState<HTMLElement | null>(null);
  const submenuCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isNew = isRecentlyUploaded(file.uploaded_at);
  const metricsTrailing = formatProjectFileMetricsTrailing({
    fileName: file.file_name,
    fileSize: file.file_size,
    authorSheets: file.file_author_sheets,
    pages: file.file_pages,
  });
  const fileTypeBadgeColors =
    file.file_type && file.file_type.length > 0
      ? getProjectFileTypeBadgeColors(file.file_type)
      : null;
  const newBadgeColors = getStatusBadgeColorByBase('Готов');

  const { stem: displayFileStem } = splitFileNameStemAndSuffix(file.file_name);
  const typeLabel = getFileTypeLabel(file.file_name);

  const linkToggleFromApi = fileLinkSelectionApi?.onToggle;

  const handleEmailFileLinkToggle = useCallback(
    (nextSelected: boolean) => {
      linkToggleFromApi?.(file.file_id, nextSelected);
    },
    [linkToggleFromApi, file.file_id]
  );

  const resolvedLinkSelection: FileLinkSelection | undefined = useMemo(
    () =>
      fileLinkSelectionApi
        ? {
            selected: fileLinkSelectionApi.selectedIds.has(file.file_id),
            onToggle: handleEmailFileLinkToggle,
          }
        : linkSelection,
    [
      fileLinkSelectionApi,
      file.file_id,
      handleEmailFileLinkToggle,
      linkSelection,
    ]
  );

  const handlePatchFileType = useCallback(
    (patch: { file_type?: string | null }) => {
      if (patchProjectFile) patchProjectFile(file.file_id, patch);
      else onPatchFile?.(patch);
    },
    [patchProjectFile, onPatchFile, file.file_id]
  );

  const handleRenameFromMenu = useCallback(() => {
    if (onRequestRenameById) onRequestRenameById(file.file_id);
    else onRequestRename?.();
  }, [onRequestRenameById, onRequestRename, file.file_id]);

  const handleDownload = () => {
    window.open(file.file_url, '_blank', 'noopener,noreferrer');
  };

  const renameEnabled =
    typeof onRequestRenameById === 'function' || typeof onRequestRename === 'function';

  const handleCopyLink = useCallback(async () => {
    const url = file.file_url?.trim();
    if (!url) {
      onCopiedLinkError?.();
      return;
    }
    const ok = await copyPlainTextToClipboard(url);
    if (ok) onCopiedLinkSuccess?.();
    else onCopiedLinkError?.();
  }, [file.file_url, onCopiedLinkSuccess, onCopiedLinkError]);

  const rowClass =
    'project-files-popover__item' +
    (resolvedLinkSelection ? ' project-files-popover__item--with-link-select' : '');

  const handleRowClick = (e: ReactMouseEvent) => {
    if (!resolvedLinkSelection) return;
    const el = e.target as HTMLElement;
    if (
      el.closest('button') ||
      el.closest('input') ||
      el.closest('label') ||
      el.closest('.project-file-audio-player')
    )
      return;
    resolvedLinkSelection.onToggle(!resolvedLinkSelection.selected);
  };

  const actionsLocked = deleteDisabled || isDeleting;
  const showAudioPlayer = isAudioFileName(file.file_name) && !!file.file_url?.trim();

  const handleBackgroundContextMenu = (e: ReactMouseEvent) => {
    const el = e.target as HTMLElement;
    if (el.closest('.project-files-popover__item-actions')) return;
    if (el.closest('.project-file-audio-player')) return;

    const canOfferCopy = !!file.file_url?.trim();
    const canOfferRename = renameEnabled;
    const canOfferFileType =
      fileTypeMenuEnabled && (typeof patchProjectFile === 'function' || typeof onPatchFile === 'function');
    if (!canOfferCopy && !canOfferRename && !canOfferFileType) return;

    e.preventDefault();
    e.stopPropagation();
    setContextMenuPos({ x: e.clientX, y: e.clientY });
  };

  const cancelFileTypeSubmenuClose = useCallback(() => {
    if (submenuCloseTimerRef.current) {
      clearTimeout(submenuCloseTimerRef.current);
      submenuCloseTimerRef.current = null;
    }
  }, []);

  const scheduleFileTypeSubmenuClose = useCallback(() => {
    submenuCloseTimerRef.current = setTimeout(() => {
      setFileTypeSubmenuAnchorEl(null);
      submenuCloseTimerRef.current = null;
    }, 150);
  }, []);

  const closeFileContextMenu = useCallback(() => {
    setContextMenuPos(null);
    setFileTypeSubmenuAnchorEl(null);
    cancelFileTypeSubmenuClose();
  }, [cancelFileTypeSubmenuClose]);

  return (
    <Fragment>
      <li
        className={rowClass}
        onClick={resolvedLinkSelection ? handleRowClick : undefined}
        onContextMenu={handleBackgroundContextMenu}
      >
        {resolvedLinkSelection ? (
          <div className="project-files-popover__link-check-label" onClick={(ev) => ev.stopPropagation()}>
            <Switch
              checked={resolvedLinkSelection.selected}
              onCheckedChange={resolvedLinkSelection.onToggle}
              aria-label={
                resolvedLinkSelection.selected
                  ? `Убрать из письма: ${file.file_name}`
                  : `Прикрепить ссылку в письмо: ${file.file_name}`
              }
            />
          </div>
        ) : null}
        <div className="project-files-popover__item-body">
          <div className="project-files-popover__item-main">
            <span className="project-files-popover__file-ext-label">{typeLabel}</span>
            <span className="project-files-popover__file-name" title={file.file_name}>
              {displayFileStem}
            </span>
            <div className="project-files-popover__item-actions">
              <IconButton
                aria-label="Скачать файл"
                size="s"
                variant="default"
                onClick={handleDownload}
                title="Скачать"
                disabled={actionsLocked}
              >
                <DownloadIcon />
              </IconButton>
              <IconButton
                aria-label="Удалить файл"
                size="s"
                variant="danger"
                onClick={() => onDelete(file.file_id)}
                disabled={isDeleting || deleteDisabled}
                title="Удалить"
              >
                <DeleteIcon />
              </IconButton>
            </div>
          </div>
          {showAudioPlayer ? (
            <ProjectFileAudioPlayer
              instanceId={file.file_id}
              src={file.file_url}
              labelForA11y={file.file_name}
              disabled={actionsLocked}
            />
          ) : null}
          <div className="project-files-popover__item-footer">
            <div className="project-files-popover__item-meta">
              <span className="project-files-popover__item-date">
                {formatFileDate(file.uploaded_at)}
              </span>
              {file.uploaded_by_name && (
                <>
                  <span className="project-files-popover__item-sep" aria-hidden="true" />
                  <span className="project-files-popover__item-author">{file.uploaded_by_name}</span>
                </>
              )}
              {fileTypeBadgeColors ? (
                <Badge variant="custom" bg={fileTypeBadgeColors.bg} color={fileTypeBadgeColors.color}>
                  {file.file_type}
                </Badge>
              ) : null}
              {isNew ? (
                <Badge
                  variant="custom"
                  bg={newBadgeColors.bg}
                  color={newBadgeColors.color}
                >
                  Новый
                </Badge>
              ) : null}
            </div>
            {metricsTrailing ? (
              <div className="project-files-popover__item-footer-trailing">
                <span className="project-files-popover__file-size">{metricsTrailing}</span>
              </div>
            ) : null}
          </div>
        </div>
      </li>
      <ContextMenu
        isOpen={contextMenuPos !== null}
        onClose={closeFileContextMenu}
        anchorPosition={contextMenuPos}
        zIndex={PROJECT_FILES_ROW_CONTEXT_MENU_Z}
      >
        <ul className="context-menu__list">
          <li className="context-menu__list-item">
            <button
              type="button"
              className="context-menu__item"
              disabled={actionsLocked || !file.file_url?.trim()}
              onClick={() => {
                void handleCopyLink();
                closeFileContextMenu();
              }}
              role="menuitem"
            >
              <span className="context-menu__iconCell" aria-hidden>
                <i className="fa-solid fa-link project-files-popover-context-menu__icon" />
              </span>
              <span className="context-menu__item-text">Копировать ссылку</span>
            </button>
          </li>
          {renameEnabled ? (
            <li className="context-menu__list-item">
              <button
                type="button"
                className="context-menu__item"
                disabled={actionsLocked}
                onClick={() => {
                  handleRenameFromMenu();
                  closeFileContextMenu();
                }}
                role="menuitem"
              >
                <span className="context-menu__iconCell" aria-hidden>
                  <EditIcon size={18} />
                </span>
                <span className="context-menu__item-text">Изменить название</span>
              </button>
            </li>
          ) : null}
          {fileTypeMenuEnabled && (patchProjectFile || onPatchFile) ? (
            <li
              className="context-menu__list-item context-menu__list-item_with-sub"
              onMouseEnter={(e) => {
                cancelFileTypeSubmenuClose();
                setFileTypeSubmenuAnchorEl(e.currentTarget);
              }}
              onMouseLeave={scheduleFileTypeSubmenuClose}
            >
              <button
                type="button"
                className="context-menu__item context-menu__action_with-sub"
                disabled={actionsLocked}
              >
                <span className="context-menu__iconCell" aria-hidden>
                  <FormatColorTextIcon sx={{ fontSize: 18 }} />
                </span>
                <span className="context-menu__item-text">Тип файла</span>
                <ChevronRightIcon size={16} className="context-menu__action-chevron" />
              </button>
              {fileTypeSubmenuAnchorEl ? (
                <ContextMenuSubmenu
                  anchorEl={fileTypeSubmenuAnchorEl}
                  zIndex={PROJECT_FILES_ROW_CONTEXT_MENU_Z + 100}
                  onMouseEnter={cancelFileTypeSubmenuClose}
                  onMouseLeave={() => setFileTypeSubmenuAnchorEl(null)}
                >
                  <ul className="context-menu__list">
                    {PROJECT_FILE_TYPE_VALUES.map((label) => (
                      <li key={label} className="context-menu__list-item">
                        <button
                          type="button"
                          className="context-menu__action"
                          disabled={actionsLocked}
                          onClick={() => {
                            handlePatchFileType({ file_type: label });
                            closeFileContextMenu();
                          }}
                        >
                          <span
                            className="project-files-popover__type-dot"
                            style={{ backgroundColor: getProjectFileTypeMenuDotColor(label) }}
                            aria-hidden
                          />
                          <span className="context-menu__label-text">{label}</span>
                        </button>
                      </li>
                    ))}
                    <li className="context-menu__list-item">
                      <button
                        type="button"
                        className="context-menu__action"
                        disabled={actionsLocked}
                        onClick={() => {
                          handlePatchFileType({ file_type: null });
                          closeFileContextMenu();
                        }}
                      >
                        <span
                          className="project-files-popover__type-dot project-files-popover__type-dot_empty"
                          aria-hidden
                        />
                        <span className="context-menu__label-text">Очистить тип</span>
                      </button>
                    </li>
                  </ul>
                </ContextMenuSubmenu>
              ) : null}
            </li>
          ) : null}
        </ul>
      </ContextMenu>
    </Fragment>
  );
});

export const ProjectFilesPopoverLocalDraftRow = memo(function ProjectFilesPopoverLocalDraftRow({
  item,
  onPatchDraft,
  onRemove,
  onRequestRename,
  audioInstanceId,
  draftActionsLocked = false,
  onDraftFileMarkedText,
}: {
  item: ProjectFileDraftItem;
  onPatchDraft: (patch: Partial<ProjectFileDraftItem>) => void;
  onRemove: () => void;
  onRequestRename: () => void;
  audioInstanceId: string;
  /** Пока идёт клиентский расчёт АЛ для новых файлов — блокируем действия со строкой */
  draftActionsLocked?: boolean;
  /** После присвоения типа «Текст» (например, подстановка АЛ/страниц в форму создания проекта). */
  onDraftFileMarkedText?: (item: ProjectFileDraftItem) => void;
}) {
  const [contextMenuPos, setContextMenuPos] = useState<{ x: number; y: number } | null>(null);
  const [fileTypeSubmenuAnchorEl, setFileTypeSubmenuAnchorEl] = useState<HTMLElement | null>(null);
  const submenuCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const file = item.file;
  const metricsTrailing = formatProjectFileMetricsTrailing({
    fileName: file.name,
    fileSize: file.size,
    authorSheets: item.file_author_sheets,
    pages: item.file_pages,
    metricsUnavailable: item.file_metrics_unavailable,
  });
  const extLabel = getFileTypeLabel(file.name);
  const showAudioPlayer = isAudioFileName(file.name);
  const fileTypeBadgeColors =
    item.file_type && item.file_type.length > 0 ? getProjectFileTypeBadgeColors(item.file_type) : null;

  const { stem: displayStem } = splitFileNameStemAndSuffix(file.name);

  const objectUrl = useMemo(() => URL.createObjectURL(file), [file]);
  useEffect(() => () => URL.revokeObjectURL(objectUrl), [objectUrl]);

  const handleDownload = useCallback(() => {
    window.open(objectUrl, '_blank', 'noopener,noreferrer');
  }, [objectUrl]);

  const handlePatchFileType = useCallback(
    (patch: { file_type?: string | null }) => {
      const wasAlreadyText = item.file_type === PROJECT_FILE_TYPE_TEXT;
      onPatchDraft(patch);
      if (patch.file_type === PROJECT_FILE_TYPE_TEXT && !wasAlreadyText) {
        onDraftFileMarkedText?.(item);
      }
    },
    [onPatchDraft, onDraftFileMarkedText, item]
  );

  const cancelFileTypeSubmenuClose = useCallback(() => {
    if (submenuCloseTimerRef.current) {
      clearTimeout(submenuCloseTimerRef.current);
      submenuCloseTimerRef.current = null;
    }
  }, []);

  const scheduleFileTypeSubmenuClose = useCallback(() => {
    submenuCloseTimerRef.current = setTimeout(() => {
      setFileTypeSubmenuAnchorEl(null);
      submenuCloseTimerRef.current = null;
    }, 150);
  }, []);

  const closeFileContextMenu = useCallback(() => {
    setContextMenuPos(null);
    setFileTypeSubmenuAnchorEl(null);
    cancelFileTypeSubmenuClose();
  }, [cancelFileTypeSubmenuClose]);

  const handleBackgroundContextMenu = (e: ReactMouseEvent) => {
    if (draftActionsLocked) return;
    const el = e.target as HTMLElement;
    if (el.closest('.project-files-popover__item-actions')) return;
    if (el.closest('.project-file-audio-player')) return;
    e.preventDefault();
    e.stopPropagation();
    setContextMenuPos({ x: e.clientX, y: e.clientY });
  };

  const handleRenameFromMenu = useCallback(() => {
    onRequestRename();
  }, [onRequestRename]);

  return (
    <Fragment>
      <li className="project-files-popover__item" onContextMenu={handleBackgroundContextMenu}>
        <div className="project-files-popover__item-body">
          <div className="project-files-popover__item-main">
            <span className="project-files-popover__file-ext-label">{extLabel}</span>
            <span className="project-files-popover__file-name" title={file.name}>
              {displayStem}
            </span>
            <div className="project-files-popover__item-actions">
              <IconButton
                aria-label="Открыть файл"
                size="s"
                variant="default"
                onClick={handleDownload}
                title="Открыть"
                disabled={draftActionsLocked}
              >
                <DownloadIcon />
              </IconButton>
              <IconButton
                aria-label="Убрать файл из списка"
                size="s"
                variant="danger"
                onClick={onRemove}
                title="Убрать"
                disabled={draftActionsLocked}
              >
                <DeleteIcon />
              </IconButton>
            </div>
          </div>
          {showAudioPlayer ? (
            <ProjectFileAudioPlayer
              instanceId={audioInstanceId}
              src={objectUrl}
              labelForA11y={file.name}
              disabled={draftActionsLocked}
            />
          ) : null}
          <div className="project-files-popover__item-footer">
            <div className="project-files-popover__item-meta">
              <span className="project-files-popover__item-date">Будет загружено при создании проекта</span>
              {fileTypeBadgeColors ? (
                <Badge variant="custom" bg={fileTypeBadgeColors.bg} color={fileTypeBadgeColors.color}>
                  {item.file_type}
                </Badge>
              ) : null}
              <Badge
                variant="custom"
                bg="color-mix(in srgb, var(--accent-warning) 15%, transparent)"
                color="var(--accent-warning)"
              >
                Черновик
              </Badge>
            </div>
            {metricsTrailing ? (
              <div className="project-files-popover__item-footer-trailing">
                <span className="project-files-popover__file-size">{metricsTrailing}</span>
              </div>
            ) : null}
          </div>
        </div>
      </li>
      <ContextMenu
        isOpen={contextMenuPos !== null}
        onClose={closeFileContextMenu}
        anchorPosition={contextMenuPos}
        zIndex={PROJECT_FILES_ROW_CONTEXT_MENU_Z}
      >
        <ul className="context-menu__list">
          <li className="context-menu__list-item">
            <button
              type="button"
              className="context-menu__item"
              onClick={() => {
                handleRenameFromMenu();
                closeFileContextMenu();
              }}
              role="menuitem"
            >
              <span className="context-menu__iconCell" aria-hidden>
                <EditIcon size={18} />
              </span>
              <span className="context-menu__item-text">Изменить название</span>
            </button>
          </li>
          <li
            className="context-menu__list-item context-menu__list-item_with-sub"
            onMouseEnter={(e) => {
              cancelFileTypeSubmenuClose();
              setFileTypeSubmenuAnchorEl(e.currentTarget);
            }}
            onMouseLeave={scheduleFileTypeSubmenuClose}
          >
            <button type="button" className="context-menu__item context-menu__action_with-sub">
              <span className="context-menu__iconCell" aria-hidden>
                <FormatColorTextIcon sx={{ fontSize: 18 }} />
              </span>
              <span className="context-menu__item-text">Тип файла</span>
              <ChevronRightIcon size={16} className="context-menu__action-chevron" />
            </button>
            {fileTypeSubmenuAnchorEl ? (
              <ContextMenuSubmenu
                anchorEl={fileTypeSubmenuAnchorEl}
                zIndex={PROJECT_FILES_ROW_CONTEXT_MENU_Z + 100}
                onMouseEnter={cancelFileTypeSubmenuClose}
                onMouseLeave={() => setFileTypeSubmenuAnchorEl(null)}
              >
                <ul className="context-menu__list">
                  {PROJECT_FILE_TYPE_VALUES.map((label) => (
                    <li key={label} className="context-menu__list-item">
                      <button
                        type="button"
                        className="context-menu__action"
                        onClick={() => {
                          handlePatchFileType({ file_type: label });
                          closeFileContextMenu();
                        }}
                      >
                        <span
                          className="project-files-popover__type-dot"
                          style={{ backgroundColor: getProjectFileTypeMenuDotColor(label) }}
                          aria-hidden
                        />
                        <span className="context-menu__label-text">{label}</span>
                      </button>
                    </li>
                  ))}
                  <li className="context-menu__list-item">
                    <button
                      type="button"
                      className="context-menu__action"
                      onClick={() => {
                        handlePatchFileType({ file_type: null });
                        closeFileContextMenu();
                      }}
                    >
                      <span
                        className="project-files-popover__type-dot project-files-popover__type-dot_empty"
                        aria-hidden
                      />
                      <span className="context-menu__label-text">Очистить тип</span>
                    </button>
                  </li>
                </ul>
              </ContextMenuSubmenu>
            ) : null}
          </li>
        </ul>
      </ContextMenu>
    </Fragment>
  );
});
